import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import cn from 'classnames';
import { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { Button, Table, View } from 'src/components/common';
import './styles.scss';
import { IRootState } from 'src/redux/rootReducer';
import {
  exportCSVEmployeesAsync,
  getEmployeesAsync,
  setOpenEmployeeModal,
  setSelectedEmployee,
} from 'src/redux/employees/actions';
import { EmployeeInfo, EmployeeModalType, GetEmployeesParams } from 'src/redux/employees/types';
import EmployeesModal, { ModalTypes } from './EmployeesModal';
import { useComponentWillUnmount } from 'src/hooks';
import { LockupValueByLabel } from 'src/utils';
import { isEmpty } from 'src/validations';

const INITIAL_EMPLOYEE: EmployeeInfo = {
  employeeId: '',
  lastName: '',
  firstName: '',
  trade: '',
  rate: '',
  level: '',
  status: '',
};

const EmployeesContainer: React.FC<Props> = ({
  loading,
  employees,
  totalEmployees,
  openModalType,
  isRefresh,
  trades,
  status,
  levels,
  onGetEmployees,
  onSetOpenModal,
  onSetSelectedEmployee,
  onExportCSVEmployees,
}) => {
  useComponentWillUnmount(() => onSetOpenModal(null as EmployeeModalType));

  const filterParamsEmployee = (params: GetEmployeesParams) => {
    let res = params;
    Object.keys(res).forEach(key => {
      if (isEmpty(res[key])) delete res[key];
      if (key === 'trade') {
        res['tradeId'] = LockupValueByLabel(res.trade[0] as string, trades);
        delete res.trade;
      }
      if (key === 'level') {
        res['levelId'] = LockupValueByLabel(res.level[0] as string, levels);
        delete res.level;
      }
      if (key === 'status') {
        res.status = res.status.toString().toLowerCase();
      }
    });
    return res;
  };

  const handleGetEmployees = (params: GetEmployeesParams) => {
    onGetEmployees(filterParamsEmployee(params));
  };

  const handleRowClick = (_value: any, meta: { rowIndex: number }) => {
    const index = meta.rowIndex;
    onSetSelectedEmployee(employees[index]);
    onSetOpenModal(ModalTypes.VIEW as EmployeeModalType);
  };

  const handleCloseModal = () => {
    onSetOpenModal(false as EmployeeModalType);
    onSetSelectedEmployee(INITIAL_EMPLOYEE);
  };

  const handleDownloadCSV = () => {
    onExportCSVEmployees();
    return false;
  };

  const tableColumns: MUIDataTableColumn[] = useMemo(
    () => [
      { name: 'employeeId', label: 'Employee ID', options: { filter: false, sort: true } },
      { name: 'lastName', label: 'Last Name', options: { filter: false, sort: true } },
      { name: 'firstName', label: 'First Name', options: { filter: false, sort: true } },
      {
        name: 'trade',
        label: 'Trade',
        options: {
          filter: true,
          filterOptions: {
            names: trades.map(item => item.label),
          },
          sort: true,
        },
      },
      {
        name: 'level',
        label: 'Level',
        options: {
          filter: true,
          filterOptions: {
            names: levels.map(item => item.label),
          },
          sort: true,
        },
      },
      {
        name: 'rate',
        label: 'Rate',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value: string) => <span>{parseFloat(value).toFixed(2)}</span>,
        },
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          filter: true,
          sort: true,
          filterOptions: {
            names: status.map(item => item.label),
          },
          customBodyRender: (value: string) => (
            <span className={cn({ 'active-status': value === 'active' }, { 'inactive-status': value === 'inactive' })}>
              {_.capitalize(value)}
            </span>
          ),
        },
      },
    ],
    [status, levels, trades],
  );

  const tableOptions: MUIDataTableOptions = {
    count: totalEmployees,
    onRowClick: handleRowClick,
    searchPlaceholder: 'Search by employee ID or employee name',
    download: true,
    onDownload: () => handleDownloadCSV(),
  };

  return (
    <View className="ctn-employees mt-32">
      <View className="ctn-employees__header mb-32">
        <View>
          <h3>Manage Employees</h3>
        </View>
        <View>
          <Button
            onClick={() => {
              onSetOpenModal(ModalTypes.ADD as EmployeeModalType);
              onSetSelectedEmployee(INITIAL_EMPLOYEE);
            }}>
            ADD NEW EMPLOYEE
          </Button>
        </View>
      </View>

      <Table
        title=""
        data={employees}
        tableOptions={tableOptions}
        isLoading={loading}
        columns={tableColumns}
        refresh={isRefresh}
        onAction={handleGetEmployees}
      />
      <EmployeesModal
        openModalType={openModalType}
        onClose={handleCloseModal}
        onEdit={() => onSetOpenModal(ModalTypes.EDIT as EmployeeModalType)}
      />
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const mapStateToProps = (state: IRootState) => ({
  loading: state.employees.isLoading,
  employees: state.employees.employees,
  totalEmployees: state.employees.totalEmployees,
  openModalType: state.employees.openEmployeeModal,
  isRefresh: state.employees.isRefresh,
  trades: state.content.trades,
  levels: state.content.levels,
  status: state.content.status,
});

const mapDispatchToProps = {
  onGetEmployees: getEmployeesAsync.request,
  onSetSelectedEmployee: setSelectedEmployee,
  onSetOpenModal: setOpenEmployeeModal,
  onExportCSVEmployees: exportCSVEmployeesAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeesContainer);
